// Florida International University: public college page (#2743), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - FIU's Common Data Set 2025-2026 (CDS2025.pdf, posted by Analysis and
//    Information Management at aim.fiu.edu): C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table; C8 test policy for
//    Fall 2027; C21/C22 plans; G1/G5 2026-2027 costs. Quirk: the C1 totals by
//    sex give 36,113 applicants, the residency table 36,103; the page uses the
//    C1 total and shows the residency rows as the CDS prints them. Admits
//    (21,976) and enrollees (5,854) agree in both tables.
//  - FIU Admissions: First-Year Applicant (the Fall 2027 dates table, the
//    Common App, the ACT science change), Admission Standards (test
//    requirement) and First Year Pathways.
//  - The Nicole Wertheim College of Nursing and Health Sciences' FTIC
//    Pre-Nursing Pathway page and the Wertheim School of Music's Apply page
//    (audition dates for fall 2027 applicants).
//  - College Scorecard, school id 133951 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items are used (on-campus food and housing). FIU's
// OneStop 2026-2027 cost of attendance uses different living estimates
// ($29,804 for a Florida resident, loan fees included), so it is not mixed in.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'FIU Common Data Set 2025-2026',
        url: 'https://aim.fiu.edu/cds/CDS2025.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'FIU Admissions, First-Year Applicants',
        url: 'https://admissions.fiu.edu/how-to-apply/freshman-applicant/',
        read: '2026-10-01',
    },
    standards: {
        label: 'FIU Admissions, Admission Standards',
        url: 'https://admissions.fiu.edu/admission-standards/',
        read: '2026-10-01',
    },
    pathways: {
        label: 'FIU Admissions, First Year Pathways',
        url: 'https://admissions.fiu.edu/admission-standards/freshman-pathways/index.html',
        read: '2026-10-01',
    },
    nursing: {
        label: 'FIU Nicole Wertheim College of Nursing and Health Sciences, FTIC Pre-Nursing Pathway',
        url: 'https://cnhs.fiu.edu/academics/nursing/undergrad-nursing/programs/ftic-pre-nursing/',
        read: '2026-10-01',
    },
    music: {
        label: 'FIU Wertheim School of Music and Performing Arts, Apply',
        url: 'https://carta.fiu.edu/music/apply/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?133951-Florida-International-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'florida-international-university',
    kbId: 'florida_international_university',
    name: 'Florida International University',
    shortName: 'FIU',
    aliases: ['FIU'],
    location: 'Miami, FL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (FIU brand colors page); decoration only.
    branding: {
        primary: '#081E3F',
        secondary: '#B6862C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/florida_international_university_logo.png',
    },

    // The direct answer and short FAQ (#4090, founder 2026-10-13: content
    // only, no title change before the Oct 27 title-test read). Every figure
    // restates one the page already shows from the source named beside it;
    // CollegePages.test.jsx fails a number the rest of the page does not hold.
    // The search it answers: fiu early action deadline.
    faq: {
        answer: { text: 'The FIU early action deadline for fall 2027 entry is November 2, 2026, for a complete application with documents through the Common App, and early action decisions come out December 10, 2026.', source: 'firstYear' },
        questions: [
            { q: 'Is FIU early action binding?', a: 'No. FIU\'s early action plan is not binding, and FIU has no early decision plan.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'What are FIU\'s other deadlines for fall 2027?', a: 'All first-year applicants: December 16, 2026, with decisions January 21, 2027. The final deadline is March 2, 2027, with last decisions by April 2, 2027. The enrollment deposit is due May 1, 2027.', source: 'firstYear' },
            { q: 'When do FIU music applicants audition?', a: 'Every applicant to the Wertheim School of Music must pass a prescreening and audition. Early Action applicants for fall 2027 should audition by November 14, 2026.', source: 'music' },
            { q: 'Does FIU require the SAT or ACT?', a: 'Yes. FIU requires SAT, ACT or CLT scores from every first-time-in-college applicant.', source: 'standards' },
            { q: 'What is the FIU acceptance rate?', a: 'For fall 2025 entry, FIU admitted 21,976 of 36,113 first-year applicants, an acceptance rate of 60.9%. Florida residents were admitted at 80.2%.', source: 'cds', where: 'section C1' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 36113,
        admitted: 21976,
        enrolled: 5854,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 20734, admitted: 16632 },
            { group: 'Out-of-state', applied: 8882, admitted: 3155 },
            { group: 'International', applied: 6487, admitted: 2189 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'FIU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'FIU has no early decision plan. Its early action plan is not binding, and the Common Data Set does not report how many applied or were admitted early.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to FIU through the Common App. Some programs and admission paths work differently:',
        rows: [
            { school: 'Admission pathways', requirement: 'FIU admits first-year students through pathways, among them Fall Direct (the most selective), Early Fall Direct (you start in the summer and must complete courses then) and Spring Direct. Decisions are space-available: if one pathway fills, you may be admitted to the next.', source: 'pathways' },
            { school: 'Nursing', requirement: 'Apply through the Common App with Nursing as your intended major and pick one of the approved alternate majors. A limited number of first-year students are admitted directly as Pre-Nursing majors; others start in the alternate major on the Pre-Nursing Pathway and apply for the BSN later. You can only be placed in Pre-Nursing at the time of admission.', source: 'nursing' },
            { school: 'Music', requirement: 'Every undergraduate applicant to the Wertheim School of Music must pass a prescreening and audition. Early Action applicants for fall 2027 should audition by November 14, 2026, and Regular Decision applicants by March 13, 2027.', source: 'music' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Final deadline', value: 'March 2, 2027' },
        rows: [
            { plan: 'Early Action', deadline: 'November 2, 2026', decision: 'December 10, 2026', binding: false },
            { plan: 'All first-year applicants', deadline: 'December 16, 2026', decision: 'January 21, 2027', binding: false },
            { plan: 'Final deadline', deadline: 'March 2, 2027', decision: 'Last decisions by April 2, 2027', binding: false },
        ],
        note: 'Each deadline is for a complete application with documents, through the Common App, which opens August 1. The same dates apply to Early Fall (summer start) and Fall 2027. The application fee is $30, or an SAT or ACT fee waiver. The enrollment deposit is due May 1, 2027.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'FIU requires SAT, ACT or CLT scores from every first-time-in-college applicant. Scores are one factor in the decision, under Florida Board of Governors regulation 6.002.',
        source: 'standards',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for fall 2027 applicants, with scores due by March 1, and adds that minimum subscores are required for admission and every first-year student takes a math placement test.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'ACT', line: 'FIU no longer requires the ACT science score. Since the 2025-26 application, its ACT composite and composite superscore use only English, math and reading, across test dates.', source: 'firstYear' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6168, 19806] },
            { item: 'Required fees', amounts: [398, 398] },
            { item: 'Food and housing (on campus)', amounts: [13852, 13852] },
            { item: 'Books and supplies', amounts: [1350, 1350] },
            { item: 'Transportation', amounts: [2946, 2946] },
            { item: 'Other expenses', amounts: [3474, 3474] },
        ],
        totals: [28188, 41826],
        note: 'For a full-time undergraduate living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 11462,
            byIncome: [
                { income: '$0 to $30,000', amount: 9529 },
                { income: '$30,001 to $48,000', amount: 9968 },
                { income: '$48,001 to $75,000', amount: 11969 },
                { income: '$75,001 to $110,000', amount: 14589 },
                { income: 'Over $110,000', amount: 19618 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from FIU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
